import styles from './Notifications.module.css';

import classNames from 'classnames';
import { useNavigate } from 'react-router';
import React, { useMemo, useCallback } from 'react';
import { ArrowCounterClockwise } from '@phosphor-icons/react';

import { useUserSession } from 'src/hooks/useUserSession';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';

import { formatDateTime } from 'src/utils/date';
import { renterNotificationCopy } from 'src/utils/renterNotificationCopy';

import {
  useRenterNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from 'src/queries';

import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';
import { ActionButton } from 'src/components/themed/ActionButton';

import { RenterNotificationStream } from '../../types/notification';

/**
 * The renter's notification feed (rental-clock v2.1 §5–§6). This is the renter's channel
 * for rental events — the deposit charged for a day past the end date, the deposit
 * returned, a booking cancelled for non-payment — alongside the best-effort email the api
 * sends. Telegram for renters was postponed (it needs its own bot), so this screen is where
 * the events actually land.
 *
 * The feed is scoped by account on the server, so it needs the account session; a visitor is
 * told that rather than shown an empty list.
 */
export const Notifications = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useUserSession();
  useDocumentTitle('Notifications');

  const { data, isLoading, isRefetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useRenterNotifications({ enabled: isLoggedIn });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data?.pages]);
  const unreadCount = data?.pages[0]?.unreadCount ?? 0;

  const handleOpen = useCallback(
    (id: string, entityId: string, stream: RenterNotificationStream, isUnread: boolean) => {
      if (isUnread) {
        // Best-effort: a failed read-marking must not block navigation to the deal.
        markRead.mutate(id);
      }

      if (stream === RenterNotificationStream.RENTAL) {
        navigate(`/rental/${entityId}`);
      }
    },
    [markRead, navigate]
  );

  return (
    <div className={classNames('container', styles.container)}>
      <Stack width="100%" gap={2} p={4} pb={30} maxWidth="750px">
        <Stack
          width="100%"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          pl={4}
        >
          <Typography as="h1" fontWeight="600" variant="title3" color="white">
            Notifications
          </Typography>
          <ActionButton onClick={refetch} label="Refresh notifications">
            <ArrowCounterClockwise size={24} color="white" />
          </ActionButton>
        </Stack>

        {!isLoggedIn ? (
          <Stack gap={4} alignItems="center" p={4} mt={8}>
            <Typography color="lightWhite" textAlign="center">
              Sign in to see your rental notifications
            </Typography>
            <Button
              onClick={() => navigate('/auth', { state: { from: '/notifications' } })}
              title="Sign in"
              variant="accent"
            />
          </Stack>
        ) : isLoading || isRefetching ? (
          <Loader />
        ) : items.length === 0 ? (
          <Stack gap={2} alignItems="center" p={4} mt={8}>
            <Typography color="lightWhite" textAlign="center">
              No notifications yet
            </Typography>
          </Stack>
        ) : (
          <Stack width="100%" gap={2}>
            {unreadCount > 0 ? (
              <Button
                onClick={() => markAllRead.mutate()}
                title={`Mark all as read (${unreadCount})`}
                variant="transparent"
                textColor="white"
                loading={markAllRead.isPending}
              />
            ) : null}

            {items.map((item) => {
              const copy = renterNotificationCopy(item);
              const isUnread = !item.readAt;

              return (
                <button
                  key={item.id}
                  type="button"
                  className={classNames(styles.item, { [styles.unread]: isUnread })}
                  onClick={() => handleOpen(item.id, item.entityId, item.stream, isUnread)}
                >
                  <Stack gap={1} width="100%">
                    <Stack
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={2}
                    >
                      <Typography color="white" fontWeight="600">
                        {copy.title}
                      </Typography>
                      <Typography color="lightWhite" variant="small">
                        {formatDateTime(item.createdAt)}
                      </Typography>
                    </Stack>
                    <Typography color="lightWhite" variant="small">
                      {copy.body}
                    </Typography>
                  </Stack>
                </button>
              );
            })}

            {hasNextPage ? (
              <Button
                onClick={() => fetchNextPage()}
                title="Load more"
                loading={isFetchingNextPage}
                className={styles.loadMore}
              />
            ) : null}
          </Stack>
        )}
      </Stack>
    </div>
  );
};
